/*
 * @Author: liuhang liuhang@nextmar.com
 * @Date: 2025-07-23 17:00:00
 * @Description: Try-on modal component for VR and image try-on
 */
'use client';

import { fetchMeetConditionsApi } from '@/api/public';
import Dialog from '@/components/Dialog';
import Image from '@/components/Image';
import Popup from '@/components/Popup';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import WapOnly from '@/components/Responsive/WapOnly';
import Tooltip from '@/components/Tooltip';
import { useReporting } from '@/hooks/useReporting';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting';
import { colors, font } from '@/theme/zeelool.theme';
import logger from '@/utils/client-logger';
import {
  consumeTransientFullscreenExit,
  isPageFullscreen,
  requestPageFullscreen,
} from '@/utils/fullscreen';
import { EPlatform } from '@/utils/enums/platform-enum';
import { getRouteCnName } from '@/utils/route-metadata';
import { randomString, SimpleShare } from '@/utils/tools';
import { Box, Button } from '@mui/material';
import { CloseOutlined, InfoOutlined } from '@zeelool/icon';
import dynamic from 'next/dynamic';
import { usePathname, useRouter } from 'next/navigation';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from '@/i18n/use-translations';
import type { ARTryOn } from '@zeelool/ar-try-on';
import type { VrTryOnFailureType } from './components/VrTryOn';
const BeforeCloseConfirmPopup = dynamic(() => import('./components/BeforeCloseConfirmPopup'), {
  ssr: false,
});
const CapturePhoto = dynamic(() => import('./components/CapturePhoto'), { ssr: false });
const ImageTryOn = dynamic(() => import('./components/ImageTryOn'), { ssr: false });
const TryOnFailedPopup = dynamic(() => import('./components/TryOnFailedPopup'), { ssr: false });
const VrTryOn = dynamic(() => import('./components/VrTryOn'), { ssr: false });

type TryOnMode = 1 | 2;
const TRY_ON_NESTED_OVERLAY_Z_INDEX = 1400;
const TRY_ON_FULLSCREEN_EXIT_SETTLE_MS = 850;
/** 判定为浏览器因旋转退出全屏时，在时间窗口内尝试恢复全屏（需与 orientationchange 或横屏形态同时满足）。 */
const TRY_ON_ORIENTATION_NEAR_FULLSCREEN_EXIT_MS = 900;

const MOBILE_TOP_INSET = 'calc(env(safe-area-inset-top, 0px) + 16px)';
const MOBILE_TOP_INSET_FULLSCREEN = 'max(8px, calc(env(safe-area-inset-top, 0px) - 8px))';
const MOBILE_SWITCH_BUTTON_HEIGHT = 50;
const MOBILE_SWITCH_BUTTON_BOTTOM = 'calc(env(safe-area-inset-bottom, 0px) + 30px)';
const MOBILE_SWITCH_BUTTON_BOTTOM_FULLSCREEN = 'calc(env(safe-area-inset-bottom, 0px) + 60px)';
const MOBILE_IMAGE_CONTENT_SPACER = `calc(env(safe-area-inset-bottom, 0px) + 92px)`;
const MOBILE_IMAGE_CONTENT_SPACER_FULLSCREEN = `calc(env(safe-area-inset-bottom, 0px) + 122px)`;
const MOBILE_BOTTOM_CHROME_GUARD_HEIGHT = 'env(safe-area-inset-bottom, 0px)';
const MOBILE_BOTTOM_CHROME_GUARD_HEIGHT_FULLSCREEN = 'env(safe-area-inset-bottom, 0px)';

const TryOnModal: React.FC = () => {
  const t = useTranslations('product.tryOn');
  const { deviceType } = useContext(ResponsiveContext);
  const isWeb = deviceType === EPlatform.WEB;

  // 获取状态
  const show = useStore((state) => state.tryonShow);
  const type = useStore((state) => state.tryonType);
  const product = useStore((state) => state.tryonProduct);
  const modelList = useStore((state) => state.modelList);
  const token = useStore((state) => state.token);
  const router = useRouter();
  const pathname = usePathname();
  const [isVrReady, setIsVrReady] = useState(false);

  // 分别获取方法
  const closeTryon = useStore((state) => state.closeTryon);
  const switchTryonType = useStore((state) => state.switchTryonType);

  const [showShare, setShowShare] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [tryonTraceId, setTryonTraceId] = useState('');
  const [startTime, setStartTime] = useState(0);
  const [showFailPopup, setShowFailPopup] = useState(false);
  const [vrStatus, setVrStatus] = useState(0); // 0未初始化 1成功 2失败
  const [vrFailureType, setVrFailureType] = useState<VrTryOnFailureType>('launch');
  const [pendingMode, setPendingMode] = useState<TryOnMode | null>(null);
  const [vrSessionId, setVrSessionId] = useState(0);
  const [isFullscreenActive, setIsFullscreenActive] = useState(false);

  const { trackEvent } = useReporting();

  const vrBoxRef = useRef<HTMLDivElement>(null);
  const arCaptureContainerRef = useRef<ARTryOn | null>(null);
  const pendingModeRef = useRef<TryOnMode | null>(null);
  const modeStateRef = useRef({ show, type });
  const vrSessionIdRef = useRef(0);
  const prevTryOnStateRef = useRef({ show, type });
  const prevFullscreenStateRef = useRef(false);
  const fullscreenRecoveryTimerRef = useRef<number | null>(null); // requestAnimationFrame id（旋转后退全屏 settle）
  const lastWindowOrientationTsRef = useRef(0);

  const isVrMode = type === 2;
  const isImageMode = type === 1;
  const hasVrSupport = product?.glb_model;
  const isTryOnSwitchLocked = pendingMode !== null || (isVrMode && vrStatus === 0);
  const mobileChromeBackgroundColor = isVrMode ? '#595d61' : '#ffffff';
  const mobileTopInset = isFullscreenActive ? MOBILE_TOP_INSET_FULLSCREEN : MOBILE_TOP_INSET;
  const mobileSwitchButtonBottom = isFullscreenActive
    ? MOBILE_SWITCH_BUTTON_BOTTOM_FULLSCREEN
    : MOBILE_SWITCH_BUTTON_BOTTOM;
  const mobileImageContentSpacer = isFullscreenActive
    ? MOBILE_IMAGE_CONTENT_SPACER_FULLSCREEN
    : MOBILE_IMAGE_CONTENT_SPACER;
  const mobileBottomChromeGuardHeight = isFullscreenActive
    ? MOBILE_BOTTOM_CHROME_GUARD_HEIGHT_FULLSCREEN
    : MOBILE_BOTTOM_CHROME_GUARD_HEIGHT;
  const mobileTryOnPopupSx = {
    '& .MuiDrawer-paper': {
      height: '100%',
      maxHeight: '100%',
      overflow: 'hidden',
      backgroundColor: mobileChromeBackgroundColor,
    },
    '& .popup-title': {
      padding: 0,
    },
    '& .popup-content': {
      backgroundColor: 'transparent',
      padding: 0,
      paddingBottom: 0,
      overflow: 'hidden',
    },
  };

  const tryType = useMemo(() => {
    if (isVrMode) {
      return 'AR try on';
    } else if (hasVrSupport) {
      return 'AR&Pictures';
    }
    return 'Try on with pictures';
  }, [isVrMode, hasVrSupport]);

  // 处理VR准备完成
  const updatePendingMode = useCallback((nextMode: TryOnMode | null) => {
    pendingModeRef.current = nextMode;
    setPendingMode(nextMode);
  }, []);

  const nextVrSession = useCallback(() => {
    const nextSessionId = vrSessionIdRef.current + 1;
    vrSessionIdRef.current = nextSessionId;
    setVrSessionId(nextSessionId);
    return nextSessionId;
  }, []);

  const handleVrReady = useCallback(
    (ready: number, failureType?: VrTryOnFailureType, sessionId?: number) => {
      if (sessionId !== vrSessionIdRef.current) {
        return;
      }

      if (!modeStateRef.current.show || modeStateRef.current.type !== 2) {
        return;
      }

      setIsLoading(false);
      setVrStatus(ready); // 1成功 2失败
      updatePendingMode(null);

      if (ready === 2) {
        setVrFailureType(failureType ?? 'launch');
        setShowFailPopup(true);
        return;
      }

      if (ready === 1) {
        setShowFailPopup(false);
      }
    },
    [updatePendingMode],
  );

  // 切换到相机模式
  const scheduleRequestFullscreenAfterTryOnModeSwitch = useCallback((): void => {
    if (isWeb || typeof window === 'undefined') {
      return;
    }

    queueMicrotask(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!modeStateRef.current.show) {
            return;
          }
          void requestPageFullscreen();
        });
      });
    });
  }, [isWeb]);

  const handleSwitchToCamera = useCallback(() => {
    if (!product?.glb_model || type === 2 || pendingModeRef.current !== null) {
      return;
    }

    updatePendingMode(2);
    setIsLoading(true);
    setShowFailPopup(false);
    setVrFailureType('launch');
    switchTryonType(2);
    scheduleRequestFullscreenAfterTryOnModeSwitch();
  }, [
    product,
    scheduleRequestFullscreenAfterTryOnModeSwitch,
    switchTryonType,
    type,
    updatePendingMode,
  ]);

  // 切换到图片模式
  const handleSwitchToImage = useCallback(() => {
    if (type === 1 || pendingModeRef.current !== null) {
      return;
    }

    nextVrSession();
    updatePendingMode(1);
    setIsVrReady(false);
    setIsLoading(true);
    setVrStatus(0);
    setShowFailPopup(false);
    switchTryonType(1);
    scheduleRequestFullscreenAfterTryOnModeSwitch();
  }, [
    nextVrSession,
    scheduleRequestFullscreenAfterTryOnModeSwitch,
    switchTryonType,
    type,
    updatePendingMode,
  ]);

  const shareOnFacebook = useCallback(() => {
    const share = new SimpleShare({
      url: `https://${location.host}/goods-detail/${product?.shop_sku}?_from=ar_fb`,
      title: product?.name,
    });
    share.facebook();
    setShowShare(false);
  }, [product]);

  const beforeTryOnClose = () => {
    const hasGuestCustomerModel =
      typeof window !== 'undefined' && !!window.sessionStorage.getItem('zl-customer-models');
    if (isImageMode && hasGuestCustomerModel && !token) {
      setShowConfirmPopup(true);
    } else {
      closeTryon();
    }
  };
  const beforeTryOnCloseRef = useRef(beforeTryOnClose);
  beforeTryOnCloseRef.current = beforeTryOnClose;

  useEffect(() => {
    modeStateRef.current = { show, type };
  }, [show, type]);

  useEffect(() => {
    const prevState = prevTryOnStateRef.current;
    const enteringVr =
      show && type === 2 && product?.glb_model && (!prevState.show || prevState.type !== 2);
    const leavingVr = prevState.show && prevState.type === 2 && (!show || type !== 2);

    if (leavingVr) {
      nextVrSession();
    }

    if (!show) {
      updatePendingMode(null);
      setIsVrReady(false);
      setIsLoading(false);
      setVrStatus(0);
      prevTryOnStateRef.current = { show, type };
      return;
    }

    if (enteringVr) {
      nextVrSession();
      updatePendingMode(2);
      setIsLoading(true);
      setIsVrReady(true);
      setVrStatus(0);
      setShowFailPopup(false);
      setVrFailureType('launch');
      prevTryOnStateRef.current = { show, type };
      return;
    }

    if (type !== 2) {
      setIsVrReady(false);
      setVrStatus(0);
      if (pendingModeRef.current === 1) {
        const frameId = window.requestAnimationFrame(() => {
          if (!modeStateRef.current.show || modeStateRef.current.type !== 1) {
            return;
          }

          updatePendingMode(null);
          setIsLoading(false);
        });

        prevTryOnStateRef.current = { show, type };
        return () => {
          window.cancelAnimationFrame(frameId);
        };
      }

      setIsLoading(false);
    }

    prevTryOnStateRef.current = { show, type };
  }, [show, type, product?.glb_model, nextVrSession, updatePendingMode]);

  useEffect(() => {
    if (!show || type !== 2 || !product?.glb_model || isWeb) {
      return;
    }

    let cancelled = false;

    fetchMeetConditionsApi({ condition_2: 2 })
      .then((res) => {
        if (cancelled || res.code !== 200) return;
        if (res.data.is_meet_conditions) {
          router.push(`/activity-page?id=${res.data.active_page_id}`);
          setTimeout(() => {
            beforeTryOnCloseRef.current();
          }, 1000);
        }
      })
      .catch((error) => {
        logger.error('fetchMeetConditionsApi failed:', error);
      });

    return () => {
      cancelled = true;
    };
  }, [show, type, product?.glb_model, isWeb, router]);

  useEffect(() => {
    if (show) {
      setTryonTraceId(`tryon_${randomString()}`);
      setStartTime(Date.now());
      SDTryONPageView();
    }
  }, [show]);

  const SDTryONPageView = () => {
    try {
      const sdEventName = `TryONPage_View`;
      const sdEventParams = {
        tryon_trace_id: tryonTraceId,
        try_type: tryType,
        refer_page_name: getRouteCnName(pathname),
        refer_url_path: window.location.href,
        shop_sku: product?.shop_sku,
        business_sku: product?.business_sku,
        product_sku: product?.sku,
        product_id: `${product?.id}`,
        product_name: product?.short_name,
        entrance_tryon_scene: 'detail',
      };
      trackEvent(
        {
          eventName: sdEventName,
          properties: sdEventParams,
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.error('Failed to track TryONPage_View:', e);
    }
  };

  const SDTryONPageLeave = () => {
    try {
      const sdEventName = `TryONPage_Leave`;
      const currTime = Date.now();
      const sdEventParams = {
        tryon_trace_id: tryonTraceId,
        view_duration: Math.ceil((currTime - startTime) / 1000),
        shop_sku: product?.shop_sku,
        business_sku: product?.business_sku,
        product_sku: product?.sku,
        product_id: `${product?.id}`,
        product_name: product?.short_name,
        try_type: tryType,
      };
      trackEvent(
        {
          eventName: sdEventName,
          properties: sdEventParams,
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.error('Failed to track TryONPage_Leave:', e);
    }
  };

  const SDTryONPageBtnClick = (btn_name) => {
    try {
      const sdEventName = `TryONPageBtn_Click`;
      const sdEventParams = {
        tryon_trace_id: tryonTraceId,
        try_type: tryType,
        btn_name,
        shop_sku: product?.shop_sku,
        business_sku: product?.business_sku,
        product_sku: product?.sku,
        product_id: `${product?.id}`,
        product_name: product?.short_name,
      };
      trackEvent(
        {
          eventName: sdEventName,
          properties: sdEventParams,
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.error('Failed to track TryONPageBtn_Click:', e);
    }
  };

  // 追踪前一个状态
  const prevShowRef = useRef(show);

  // 由开启状态变为关闭状态
  useEffect(() => {
    const prevShow = prevShowRef.current;

    // 判断是否从 true 变为 false（由开启变为关闭）
    if (prevShow && !show) {
      SDTryONPageLeave();
    }

    // 更新前一个状态
    prevShowRef.current = show;
  }, [show]);

  useEffect(() => {
    if (isWeb || !show || typeof document === 'undefined') {
      return;
    }

    const themeColorMeta = document.querySelector('meta[name="theme-color"]');

    if (!themeColorMeta) {
      return;
    }

    const previousThemeColor = themeColorMeta.getAttribute('content');
    themeColorMeta.setAttribute('content', mobileChromeBackgroundColor);

    return () => {
      if (previousThemeColor) {
        themeColorMeta.setAttribute('content', previousThemeColor);
      } else {
        themeColorMeta.removeAttribute('content');
      }
    };
  }, [isWeb, show, mobileChromeBackgroundColor]);

  useEffect(() => {
    if (isWeb || typeof document === 'undefined') {
      return;
    }

    const recordOrientationBump = (): void => {
      lastWindowOrientationTsRef.current = Date.now();
    };

    const syncFullscreenState = (): void => {
      const nextIsFullscreen = isPageFullscreen();
      const prevIsFullscreen = prevFullscreenStateRef.current;

      prevFullscreenStateRef.current = nextIsFullscreen;
      setIsFullscreenActive(nextIsFullscreen);

      if (prevIsFullscreen && !nextIsFullscreen && modeStateRef.current.show) {
        if (consumeTransientFullscreenExit()) {
          return;
        }

        if (fullscreenRecoveryTimerRef.current !== null) {
          cancelAnimationFrame(fullscreenRecoveryTimerRef.current);
          fullscreenRecoveryTimerRef.current = null;
        }

        const settleStartedAt = performance.now();

        const settleNextFrame = (): void => {
          fullscreenRecoveryTimerRef.current = requestAnimationFrame(() => {
            if (performance.now() - settleStartedAt < TRY_ON_FULLSCREEN_EXIT_SETTLE_MS) {
              settleNextFrame();
              return;
            }

            fullscreenRecoveryTimerRef.current = null;
            if (!modeStateRef.current.show || isPageFullscreen()) {
              return;
            }

            const now = Date.now();
            const orientationRecentlyChanged =
              lastWindowOrientationTsRef.current > 0 &&
              now - lastWindowOrientationTsRef.current < TRY_ON_ORIENTATION_NEAR_FULLSCREEN_EXIT_MS;
            const looksLikeForcedLandscape =
              typeof window !== 'undefined' && window.innerWidth > window.innerHeight;

            if (orientationRecentlyChanged && looksLikeForcedLandscape) {
              void requestPageFullscreen();
            }
          });
        };

        settleNextFrame();
      }
    };

    syncFullscreenState();

    window.addEventListener('orientationchange', recordOrientationBump);
    document.addEventListener('fullscreenchange', syncFullscreenState);
    document.addEventListener('webkitfullscreenchange', syncFullscreenState as EventListener);
    document.addEventListener('mozfullscreenchange', syncFullscreenState as EventListener);
    document.addEventListener('MSFullscreenChange', syncFullscreenState as EventListener);

    return () => {
      window.removeEventListener('orientationchange', recordOrientationBump);
      document.removeEventListener('fullscreenchange', syncFullscreenState);
      document.removeEventListener('webkitfullscreenchange', syncFullscreenState as EventListener);
      document.removeEventListener('mozfullscreenchange', syncFullscreenState as EventListener);
      document.removeEventListener('MSFullscreenChange', syncFullscreenState as EventListener);
      if (fullscreenRecoveryTimerRef.current !== null) {
        cancelAnimationFrame(fullscreenRecoveryTimerRef.current);
        fullscreenRecoveryTimerRef.current = null;
      }
    };
  }, [isWeb]);

  if (!show) return null;

  // 根据设备类型选择对话框组件
  const DialogComponent = isWeb ? Dialog.Info : Popup;

  return (
    <>
      <DialogComponent
        open={show}
        onClose={beforeTryOnClose}
        {...(isWeb
          ? {
              hideHeader: true,
              sx: {
                '& .MuiDialog-paper': {
                  height: '626px',
                  width: '1000px',
                  maxWidth: '1000px',
                  zIndex: 1300,
                },
                '& .MuiDialogContent-root': {
                  padding: 0,
                },
              },
            }
          : {
              position: 'bottom',
              height: '100%',
              round: false,
              closeOnClickOverlay: true,
              childrenMaxHeight: '100%',
              zIndex: 1300,
              sx: mobileTryOnPopupSx,
              showDivider: false,
            })}
      >
        <Box
          ref={vrBoxRef}
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            backgroundColor: isWeb
              ? isVrMode
                ? '#595d61'
                : '#ffffff'
              : mobileChromeBackgroundColor,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {!isWeb && (
            <Box
              aria-hidden
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                height: mobileBottomChromeGuardHeight,
                backgroundColor: mobileChromeBackgroundColor,
                pointerEvents: 'none',
                zIndex: 1,
              }}
            />
          )}
          {/* 顶部控制栏 */}
          {hasVrSupport && isVrMode && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 10,
                display: 'flex',
                justifyContent: isWeb ? 'flex-end' : 'space-between',
                alignItems: 'center',
                paddingInline: '15px',
                pt: isWeb ? '15px' : mobileTopInset,
                gap: isWeb ? '16px' : '0',
              }}
            >
              {/* 左侧按钮 */}
              <div className="flex gap-16 relative">
                <WapOnly>
                  <Image
                    src="/tryOn/share.png"
                    alt="share"
                    width={20}
                    height={20}
                    onClick={() => setShowShare(true)}
                  />
                </WapOnly>
                {isVrMode && (
                  <Tooltip
                    title={t('tooltipDisclaimer')}
                    placement={isWeb ? 'bottom-end' : 'bottom'}
                    trigger={isWeb ? 'hover' : 'click'}
                    maxWidth={280}
                    slotProps={{
                      popper: {
                        sx: {
                          '& .MuiTooltip-tooltip': {
                            margin: '0 !important',
                            marginTop: '8px !important',
                          },
                        },
                        style: {
                          zIndex: 9999,
                        },
                        modifiers: [
                          {
                            name: 'offset',
                            options: {
                              offset: [0, 8],
                            },
                          },
                          {
                            name: 'preventOverflow',
                            options: {
                              boundary: 'viewport',
                              padding: 8,
                            },
                          },
                        ],
                      },
                    }}
                  >
                    <InfoOutlined className="text-white text-[20px] cursor-pointer" />
                  </Tooltip>
                )}
              </div>
              <CloseOutlined
                className={`text-[24px]  cursor-pointer ${isVrMode ? 'text-white' : 'text-primary-black'}`}
                onClick={beforeTryOnClose}
              />
            </Box>
          )}
          {isImageMode && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                zIndex: 10,
                display: 'flex',
                justifyContent: isWeb ? 'flex-end' : 'space-between',
                alignItems: 'center',
                paddingInline: '15px',
                pt: isWeb ? '15px' : mobileTopInset,
              }}
            >
              <CloseOutlined
                className={`text-[24px]  cursor-pointer ${isVrMode ? 'text-white' : 'text-primary-black'}`}
                onClick={beforeTryOnClose}
              />
            </Box>
          )}

          {/* 试戴内容区域 */}
          <Box
            sx={{
              flex: 1,
              position: 'relative',
              display: 'flex',
              alignItems: !isWeb && isImageMode ? 'stretch' : 'center',
              justifyContent: !isWeb && isImageMode ? 'flex-start' : 'center',
              minHeight: 0,
              overflowY: !isWeb && isImageMode ? 'auto' : 'hidden',
              overflowX: 'hidden',
              WebkitOverflowScrolling: !isWeb && isImageMode ? 'touch' : undefined,
            }}
          >
            {/* 试戴组件只在动画完成后渲染 */}
            {isVrMode && isVrReady && (
              <VrTryOn
                sessionId={vrSessionId}
                onVrReady={handleVrReady}
                arContainerRef={arCaptureContainerRef}
              />
            )}
            {isImageMode && (
              <div style={{ width: '100%' }}>
                <ImageTryOn
                  show={isImageMode}
                  product={product}
                  isWeb={isWeb}
                  SDTryONPageBtnClick={SDTryONPageBtnClick}
                />
                {!isWeb && <Box sx={{ height: mobileImageContentSpacer, flexShrink: 0 }} />}
              </div>
            )}

            {/* 拍照功能组件 */}
            {isVrMode && !isWeb && (
              <CapturePhoto
                vrStatus={vrStatus}
                isWeb={isWeb}
                product={product}
                arContainerRef={arCaptureContainerRef}
                onCaptureComplete={(imageDataURL) => {
                  logger.log('Photo captured:', `${imageDataURL.slice(0, 50)}...`);
                }}
              />
            )}
          </Box>

          {/* VR加载状态覆盖层 */}
          {isLoading && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 5,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                height: '90px',
                width: '90px',
                backgroundColor: 'rgba(0,0,0,0.5)',
                borderRadius: '10px',
              }}
            >
              <Image
                src="/tryOn/loading.gif"
                alt="loading"
                width={60}
                height={60}
                className="w-[60px] h-auto"
              />
            </Box>
          )}

          {/* 底部切换按钮 */}
          {hasVrSupport && (
            <Box
              sx={{
                position: 'absolute',
                bottom: isWeb ? '0' : mobileSwitchButtonBottom,
                left: isWeb ? 'auto' : '50%',
                transform: isWeb ? 'none' : 'translateX(-50%)',
                right: isWeb ? '0' : 'auto',
                display: 'flex',
                flexDirection: isWeb ? 'row-reverse' : 'row',
                gap: isWeb ? '0' : 1,
                zIndex: 10,
              }}
            >
              <Button
                variant="contained"
                onClick={() => {
                  handleSwitchToCamera();
                  SDTryONPageBtnClick('Camera');
                }}
                disabled={!hasVrSupport || isTryOnSwitchLocked}
                sx={{
                  width: isWeb ? '188px' : '170px',
                  height: isWeb ? '78px' : `${MOBILE_SWITCH_BUTTON_HEIGHT}px`,
                  borderRadius: isWeb ? '0' : '25px',
                  backgroundColor: type === 2 ? colors.primaryPurple : colors.auxiliaryLightPurple,
                  color: type === 2 ? colors.primaryWhite : colors.primaryBlack,
                  fontFamily: font.bb,
                  fontSize: isWeb ? '17px' : '17px',
                  '&.Mui-disabled': {
                    backgroundColor:
                      type === 2 ? colors.primaryPurple : colors.auxiliaryLightPurple,
                    color: type === 2 ? colors.primaryWhite : colors.primaryBlack,
                    opacity: 0.55,
                  },
                  '&:hover': {
                    backgroundColor:
                      type === 2 ? colors.primaryPurpleHover : colors.primaryWhiteHover,
                  },
                }}
              >
                {t('cameraButton')}
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  handleSwitchToImage();
                  SDTryONPageBtnClick('Image');
                }}
                disabled={isTryOnSwitchLocked}
                sx={{
                  width: isWeb ? '188px' : '170px',
                  height: isWeb ? '78px' : `${MOBILE_SWITCH_BUTTON_HEIGHT}px`,
                  borderRadius: isWeb ? '0' : '25px',
                  backgroundColor: type === 2 ? colors.auxiliaryLightPurple : colors.primaryPurple,
                  color: type === 2 ? colors.primaryBlack : colors.primaryWhite,
                  fontFamily: font.bb,
                  fontSize: isWeb ? '17px' : '17px',
                  '&.Mui-disabled': {
                    backgroundColor:
                      type === 2 ? colors.auxiliaryLightPurple : colors.primaryPurple,
                    color: type === 2 ? colors.primaryBlack : colors.primaryWhite,
                    opacity: 0.55,
                  },
                  '&:hover': {
                    backgroundColor:
                      type === 2 ? colors.primaryWhiteHover : colors.primaryPurpleHover,
                  },
                }}
              >
                {t('imagesButton')}
              </Button>
            </Box>
          )}
        </Box>
      </DialogComponent>
      <WapOnly>
        <Popup
          open={showShare}
          onClose={() => setShowShare(false)}
          title="Share On"
          position="bottom"
          zIndex={TRY_ON_NESTED_OVERLAY_Z_INDEX}
        >
          <div className="flex flex-col items-center gap-16" onClick={shareOnFacebook}>
            <Image
              src="/tryOn/share-facebook.png"
              alt="share"
              width={60}
              height={60}
              className="w-[60px] h-auto"
            />
            <span>Facebook</span>
          </div>
        </Popup>
      </WapOnly>
      <BeforeCloseConfirmPopup
        open={showConfirmPopup}
        closeConfirm={() => {
          setShowConfirmPopup(false);
        }}
        onClose={() => {
          setShowConfirmPopup(false);
          closeTryon();
        }}
      />
      <TryOnFailedPopup
        open={showFailPopup}
        failureType={vrFailureType}
        onClose={() => setShowFailPopup(false)}
        onSwitchToImage={handleSwitchToImage}
        showContinueButton={isVrMode}
      />
    </>
  );
};

export default TryOnModal;
